import React, { useMemo } from 'react'
import { Calendar, Users, DollarSign, FileText, BarChart3, HelpCircle, CalendarPlus, XCircle, MessageCircle, Globe, BookOpen } from 'lucide-react'
import { useThemeColors } from '../../hooks/useThemeColors'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '../../lib/supabase'
import { useOrganization } from '../../contexts/OrganizationContext'

interface QuickActionsProps {
  onAction: (action: string) => void
}

export default function QuickActions({ onAction }: QuickActionsProps) {
  const { colors } = useThemeColors()
  const { organization } = useOrganization()

  // Fetch today's bookings for context awareness
  const { data: todayBookings = [] } = useQuery({
    queryKey: ['quick-actions-bookings', organization?.id],
    queryFn: async () => {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const tomorrow = new Date(today)
      tomorrow.setDate(tomorrow.getDate() + 1)

      const { data } = await supabase
        .from('bookings')
        .select('*')
        .eq('organization_id', organization?.id)
        .gte('start_time', today.toISOString())
        .lt('start_time', tomorrow.toISOString())
        .neq('status', 'cancelled')
        .order('start_time', { ascending: true })

      return data || []
    },
    enabled: !!organization?.id
  })

  // Context-aware actions based on time of day and bookings
  const contextAwareActions = useMemo(() => {
    const hour = new Date().getHours()
    const hasBookingsToday = todayBookings.length > 0
    const isMorning = hour >= 6 && hour < 12
    const isEvening = hour >= 17

    const actions = []

    // Help-focused actions - always shown first for discoverability
    actions.push({
      icon: HelpCircle,
      label: 'How do I...',
      action: "Show me how to do common tasks in Facility Pro"
    })

    actions.push({
      icon: BookOpen,
      label: 'Getting Started',
      action: "Walk me through setting up my facility step by step"
    })

    // Always show: Book next session (smart default: same client, same time slot next week)
    actions.push({
      icon: CalendarPlus,
      label: 'Book next session',
      action: "Help me book a session for next week with smart defaults"
    })

    // Context: If there are bookings today
    if (hasBookingsToday) {
      actions.push({
        icon: XCircle,
        label: "Cancel today's session",
        action: "Help me cancel a session today"
      })
    } else {
      // Alternative: Check schedule
      actions.push({
        icon: Calendar,
        label: 'Check schedule',
        action: "Show me today's schedule"
      })
    }

    // Context: Morning - send reminders
    if (isMorning && hasBookingsToday) {
      actions.push({
        icon: MessageCircle,
        label: 'Send reminders',
        action: "Send reminder messages to clients with sessions today"
      })
    } else if (isEvening) {
      // Evening - revenue report
      actions.push({
        icon: DollarSign,
        label: 'Revenue today',
        action: "What's my revenue today?"
      })
    } else {
      // Default: Find client
      actions.push({
        icon: Users,
        label: 'Find client',
        action: "Help me find a client"
      })
    }

    // Always show: Post to website
    actions.push({
      icon: Globe,
      label: 'Post to website',
      action: "Help me create a post for my website"
    })

    // Fill remaining slots with other useful actions
    if (actions.length < 6) {
      actions.push({
        icon: FileText,
        label: 'Create invoice',
        action: "Create an invoice"
      })
    }

    if (actions.length < 6) {
      actions.push({
        icon: BarChart3,
        label: 'Analytics',
        action: "Show me business analytics"
      })
    }

    return actions.slice(0, 6)
  }, [todayBookings])

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '12px'
    }}>
      {contextAwareActions.map((action, index) => {
        const Icon = action.icon
        return (
          <button
            key={index}
            onClick={() => onAction(action.action)}
            style={{
              backgroundColor: colors.primaryAlpha10,
              border: `1px solid ${colors.border}`,
              borderRadius: '12px',
              padding: '20px 16px',
              minHeight: '80px',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              color: colors.text
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.primaryAlpha20
              e.currentTarget.style.borderColor = colors.primary
              e.currentTarget.style.transform = 'translateY(-2px)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colors.primaryAlpha10
              e.currentTarget.style.borderColor = colors.border
              e.currentTarget.style.transform = 'translateY(0)'
            }}
          >
            <Icon size={24} style={{ color: colors.primary }} />
            <span style={{
              fontSize: '13px',
              fontWeight: 500,
              textAlign: 'center',
              lineHeight: '1.3'
            }}>
              {action.label}
            </span>
          </button>
        )
      })}
    </div>
  )
}